import React, { useState } from 'react';
import { Upload, Button, Space, Typography, App } from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { fixUrl } from '../../../../services/api';

const { Text } = Typography;

// Mismo tope que valida `FondoHeroTab`. Se repite y no se importa: son dos
// pantallas que no comparten nada más, y acoplarlas por una constante de una
// línea deja un import que confunde más de lo que ahorra.
const MAX_MB = 5;


/**
 * Subir / reemplazar / quitar UNA imagen de la config del catálogo.
 *
 * Sube Y persiste en una sola llamada, igual que el hero: partirlo en dos pasos
 * —subir a un temporal y mandar la URL después— es exactamente lo que dejó roto
 * a `GenerarPDF`, que llama a un `upload-temp` que no existe.
 *
 * Quitar NO es un endpoint aparte: es vaciar la clave con el PUT de config que
 * ya existe. Un endpoint más es otra puerta que mantener y proteger.
 */
const SubirImagen = ({
  valor, campo, onSubir, onQuitar, alto = 64, ayuda,
}) => {
  const { message } = App.useApp();
  const [subiendo, setSubiendo] = useState(false);
  const [quitando, setQuitando] = useState(false);

  const manejarUpload = async (file) => {
    if (!file.type.startsWith('image/')) {
      message.error('Solo se permiten archivos de imagen');
      return false;
    }
    // `>=` y no `>`: `FondoHeroTab` hace `< 5` y rechaza si NO se cumple, o sea
    // rechaza los 5MB exactos igual que esto. Los dos topes coinciden en el
    // borde, que es donde este tipo de comparacion se rompe sin que nadie mire.
    if (file.size / 1024 / 1024 >= MAX_MB) {
      message.error(`La imagen debe ser menor a ${MAX_MB}MB`);
      return false;
    }
    setSubiendo(true);
    try {
      await onSubir(file, campo);
      message.success('Imagen cargada');
    } catch (e) {
      // El motivo real viene del backend: `error.message` de axios dice
      // "Request failed with status code 413" y no qué pasó.
      message.error(e.response?.data?.error || 'No se pudo subir la imagen');
    } finally {
      setSubiendo(false);
    }
    return false;   // corta el upload automático de antd
  };

  // QUITAR TAMBIEN ES UNA ACCION DEL PRODUCTOR, y hasta acá era la única de las
  // dos sin red. `onQuitar` se llamaba sin `await` y sin `catch`: si el PUT
  // fallaba quedaba una promesa rechazada sin dueño, cero aviso, y la miniatura
  // desaparecía de pantalla sobre una imagen que la base nunca borró. El
  // productor se iba convencido de que la había quitado.
  //
  // Mismo molde que `manejarUpload`: el hijo muestra el mensaje y maneja el
  // spinner, el padre sólo tira. Y el `finally` importa igual que arriba —
  // sin él, un error deja el botón girando para siempre.
  const manejarQuitar = async () => {
    setQuitando(true);
    try {
      await onQuitar(campo);
      message.success('Imagen quitada');
    } catch (e) {
      message.error(e.response?.data?.error || 'No se pudo quitar la imagen');
    } finally {
      setQuitando(false);
    }
  };

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      {valor ? (
        <div
          style={{
            // El damero deja ver la transparencia: estas imágenes suelen ser
            // PNG con fondo transparente, y sobre blanco no se distingue un
            // logo recortado de uno con fondo blanco pegado.
            background:
              'repeating-conic-gradient(#f0f0f0 0% 25%, #fff 0% 50%) 50% / 16px 16px',
            border: '1px solid #e4e1db',
            borderRadius: 8,
            padding: 8,
            display: 'inline-block',
          }}
        >
          <img
            src={fixUrl(valor)}
            alt=""
            style={{ height: alto, display: 'block', maxWidth: '100%' }}
          />
        </div>
      ) : null}

      <Space wrap>
        <Upload accept="image/*" showUploadList={false} beforeUpload={manejarUpload}>
          <Button icon={<UploadOutlined />} loading={subiendo}>
            {valor ? 'Cambiar' : 'Subir imagen'}
          </Button>
        </Upload>
        {valor && (
          <Button
            danger
            type="text"
            icon={<DeleteOutlined />}
            onClick={manejarQuitar}
            loading={quitando}
          >
            Quitar
          </Button>
        )}
      </Space>

      {ayuda && (
        <Text type="secondary" style={{ fontSize: 12 }}>{ayuda}</Text>
      )}
    </Space>
  );
};

export default SubirImagen;
